import { useEffect, useMemo, useRef, useState } from "react"
import {
    Box,
    Checkbox,
    FormControlLabel,
    FormHelperText,
    InputAdornment,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faMessage } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"
import { Form, Formik, FormikHelpers } from "formik"
import * as Yup from "yup"
import { enqueueSnackbar } from "notistack"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { CheckInAlerts } from "./CheckInAlerts"
import { CheckInAlertsApi, useCheckInAlerts } from "./useCheckInAlerts"
import { UserToggle } from "./UserToggle"
import { FormikNumberField, FormikSubmitButton, FormikTextField } from "../../../../../../shared/components/forms/Formik"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { checkInGuest } from "../../serviceCenterHomeApi"
import { useDateTime } from "../../../../../localization/useDateTime"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectServiceCenterId } from "../../../../../login/loginSelectors"
import { CompanySelect } from "../../../../../../shared/components/createOrder/orderOnBehalf/CompanySelect"
import { UserType } from "../../../../../login/loginTypes"
import { UserDetailsProps } from "../../../../../../shared/components/createOrder/orderOnBehalf/OrderOnBehalfFlow"
import { UserSelector } from "../../../../../../shared/components/createOrder/orderOnBehalf/UserSelector"
import { OrderOnBehalfScope } from "../../../../../../shared/components/createOrder/orderOnBehalf/orderOnBehalfTypes"
import { useLabelPrinter } from "../../hooks/useLabelPrinter"
import { GroupType, SingleType } from "../../serviceCenterHomeTypes"
import { ApiError } from "../../../../../../core/api/errorTypes"
import { ServiceResponseStatus } from "../../../../../../core/api/apiTypes"
import {
    LabelPrinterDialogVariant,
    LabelPrinterExtensionDialog,
} from "../../../../../../shared/components/dialogs/LabelPrinterExtensionDialog"
import { defaultUserDetailsState } from "../../../../../../shared/components/createOrder/defaults"
import { loadCheckInPrefs, saveCheckInPrefs } from "../checkInPrefs"
import {
    MAX_COUNT_VALUE,
    MAX_NAME_LENGTH,
    MAX_ORDER_COMMENT_LENGTH,
} from "../../../../../../constants/inputValidationDefaults"

const useStyles = makeStyles()(() => ({
    modal: {
        padding: "3rem",
    },
    guestDetails: {
        width: "65%",
    },
    settingsSection: {
        width: "35%",
    },
    checkinButton: {
        padding: ".5rem 3rem !important",
        minWidth: "10rem",
    },
    checkboxLabel: {
        alignItems: "flex-start",
    },
    checkboxAlignTop: {
        alignSelf: "flex-start",
        marginTop: "-0.5rem",
    },
}))

// The success confirmation must stay long enough to read — longer when there's a message to relay to the guest.
const SUCCESS_ALERT_MS = 30_000
const SUCCESS_ALERT_WITH_GUEST_MESSAGE_MS = 60_000

// The "print guest card" / "notify host" checkboxes default to the receptionist's last choice (persisted),
// instead of resetting to true on every check-in.

type FormValues = {
    userType: "single" | "group"
    guestName: string
    guestCompany: string
    numberOfGuests: string
    printGuestCard: boolean
    sendNotification: boolean
    messageToHost: string
    companyId: string
    selectedUserId?: number
}

export function GuestCheckInModal({ open, onClose }: { open: boolean; onClose: () => void }) {
    const { classes } = useStyles()

    return (
        <StandardDialog
            dialogTitle={
                <LocalizedStrict id="service-center-reports-dashboard-check-in-modal-title">
                    <>Guest check-in</>
                </LocalizedStrict>
            }
            open={open}
            onClose={onClose}
            maxWidthProp="md"
            scroll="body"
        >
            <Box className={classes.modal}>
                <CheckInForm
                    variant="modal"
                    handleFinish={() => {
                        onClose()
                    }}
                />
            </Box>
        </StandardDialog>
    )
}

type CheckInFormProps = Readonly<{
    handleFinish?: () => void
    variant?: "modal" | "block"
    alertsApi?: CheckInAlertsApi
}>

export function CheckInForm({ handleFinish, variant, alertsApi }: CheckInFormProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()

    const formatValidTo = (validTo?: string) =>
        validTo ? dateTimeFactory.formatDate(dateTimeFactory.fromISO(validTo)) : undefined

    const serviceCenterId = useAppSelector(selectServiceCenterId)
    const scope = useMemo(
        (): Extract<OrderOnBehalfScope, { type: UserType.SERVICE_CENTER }> => ({
            type: UserType.SERVICE_CENTER,
            serviceCenterId: serviceCenterId ?? 0,
        }),
        [serviceCenterId],
    )
    const [userDetailsState, setUserDetailsState] = useState<UserDetailsProps>(defaultUserDetailsState)
    const ownAlerts = useCheckInAlerts()
    const { alerts, addAlert, removeAlert } = alertsApi ?? ownAlerts
    const [initialCheckInPrefs] = useState(loadCheckInPrefs)
    const [isPrintInProgress, setIsPrintInProgress] = useState(false)
    const currentCheckInTypeRef = useRef<"single" | "group">("single")

    const {
        printGuestLabel,
        printGroupLabels,
        hasError: hasPrintError,
        hasSuccess: hasPrintSuccess,
        isInstalledError,
        isPrinterNotFound,
        clearState,
    } = useLabelPrinter()

    const { mutateAsync } = useApiMutation(checkInGuest, {
        async onSuccess(_params, data) {
            const { serviceResponse } = data

            if (serviceResponse.status === ServiceResponseStatus.PARTIAL_SUCCESS) {
                if (serviceResponse.isUserMessage && serviceResponse.displayMessage) {
                    addAlert("error", serviceResponse.displayMessage, { host: data.host })
                }
                return
            }

            const isGroupCheckIn = currentCheckInTypeRef.current === "group"

            const successMessage = isGroupCheckIn
                ? l10n.getString("service-center-reports-check-group-success-title")
                : serviceResponse.displayMessage || l10n.getString("service-center-reports-check-success-title")

            const hasGuestMessage = Boolean(data.messageToGuest || data.messageResponse?.guestMessage)

            addAlert("success", successMessage, {
                autoDismissMs: hasGuestMessage ? SUCCESS_ALERT_WITH_GUEST_MESSAGE_MS : SUCCESS_ALERT_MS,
            })
        },
        async onError(_, error) {
            let errorDescription = l10n.getString("service-center-check-in-error-generic")

            if (error instanceof ApiError) {
                // Only a 404 from the API (JSON) means the guest/host was not found.
                // A 404 from Tomcat (HTML, unknown route) is not, so it keeps the generic message.
                const contentType = error.originalErrorResponse?.headers?.["content-type"]
                const isTomcatHtmlResponse = typeof contentType === "string" && contentType.includes("text/html")

                if (error.hasApiMessage) {
                    errorDescription = error.displayMessage
                } else if (error.status === 404 && !isTomcatHtmlResponse) {
                    errorDescription = l10n.getString("service-center-check-in-error-not-found")
                } else if (error.status === 400) {
                    errorDescription = l10n.getString("service-center-check-in-error-invalid-data")
                }
            } else if (error && typeof error === "object" && "displayMessage" in error) {
                errorDescription = error.displayMessage as string
            }

            addAlert("error", errorDescription)
        },
    })

    useEffect(() => {
        if (!isPrintInProgress) {
            return
        }

        if (hasPrintSuccess) {
            setIsPrintInProgress(false)
            clearState()
            handleFinish?.()
            return
        }

        if (hasPrintError) {
            // Guest is already checked in — a print failure is a snackbar, never a check-in error.
            setIsPrintInProgress(false)
            enqueueSnackbar({
                variant: "error",
                message: l10n.getString("service-center-check-in-print-failed-snackbar"),
            })
            clearState()
            return
        }

        if (isInstalledError || isPrinterNotFound) {
            setIsPrintInProgress(false)
        }
    }, [hasPrintSuccess, hasPrintError, isInstalledError, isPrinterNotFound, isPrintInProgress])

    const validationSchema = Yup.object().shape({
        userType: Yup.string()
            .oneOf(["single", "group"], "Invalid user type")
            .required(l10n.getString("required-field")),
        guestName: Yup.string().when("userType", {
            is: "single",
            then: (schema) =>
                schema
                    .required(l10n.getString("required-field"))
                    .min(3, l10n.getString("service-center-reports-check-in-modal-guest-name-min", { min: 3 }))
                    .max(
                        MAX_NAME_LENGTH,
                        l10n.getString("service-center-reports-check-in-modal-guest-name-max", {
                            max: MAX_NAME_LENGTH,
                        }),
                    ),
            otherwise: (schema) => schema.notRequired(),
        }),
        companyId: Yup.string().required(l10n.getString("required-field")),
        selectedUserId: Yup.number().required(l10n.getString("required-field")),
        numberOfGuests: Yup.number()
            .nullable()
            .transform((value) => (isNaN(value) ? undefined : value))
            .when("userType", {
                is: "group",
                then: (schema) =>
                    schema
                        .required(l10n.getString("required-field"))
                        .min(
                            1,
                            l10n.getString("service-center-reports-check-in-modal-number-of-guests-min", { min: 1 }),
                        )
                        .max(
                            MAX_COUNT_VALUE,
                            l10n.getString("service-center-reports-check-in-modal-number-of-guests-max", {
                                max: MAX_COUNT_VALUE,
                            }),
                        ),
                otherwise: (schema) => schema.notRequired(),
            }),
        guestCompany: Yup.string()
            .required(l10n.getString("required-field"))
            .max(
                MAX_NAME_LENGTH,
                l10n.getString("service-center-reports-check-in-modal-guest-company-max", { max: MAX_NAME_LENGTH }),
            ),
        printGuestCard: Yup.boolean(),
        sendNotification: Yup.boolean(),
        messageToHost: Yup.string().max(
            MAX_ORDER_COMMENT_LENGTH,
            l10n.getString("service-center-reports-check-in-modal-message-max", { max: MAX_ORDER_COMMENT_LENGTH }),
        ),
    })

    const initialValues: FormValues = {
        userType: "single",
        guestName: "",
        numberOfGuests: "",
        guestCompany: "",
        printGuestCard: initialCheckInPrefs.printGuestCard,
        sendNotification: initialCheckInPrefs.sendNotification,
        messageToHost: "",
        companyId: "",
        selectedUserId: undefined,
    }

    async function handleSubmit(values: FormValues, helpers: FormikHelpers<FormValues>) {
        try {
            let checkIns: SingleType[] | GroupType[] = []

            if (values.userType === "single") {
                checkIns = [
                    {
                        type: "SINGLE",
                        host: {
                            id: userDetailsState?.selectedUserId ?? 0,
                        },
                        guest: {
                            company: values.guestCompany,
                            name: values.guestName,
                        },
                    },
                ]
            }
            if (values.userType === "group") {
                checkIns = [
                    {
                        type: "GROUP",
                        host: {
                            id: userDetailsState?.selectedUserId ?? 0,
                        },
                        group: {
                            company: values.guestCompany,
                            count: +values.numberOfGuests,
                        },
                    },
                ]
            }

            // Set the check-in type for the success callback
            currentCheckInTypeRef.current = values.userType

            const response = await mutateAsync({
                serviceCenterId: serviceCenterId ?? 0,
                checkIns,
                notifyHost: values.sendNotification,
                messageToHost: values.messageToHost,
            })

            if (values.printGuestCard) {
                const guestCards = response.guestCardDetails?.guestCards
                const singleCard = guestCards?.[0]
                const groupCards = guestCards?.map((card) => ({
                    checkOutCode: card.checkOutCode,
                    validTo: formatValidTo(card.validTo),
                }))

                // Clear any previous printer state before starting new print
                clearState()
                setIsPrintInProgress(true)

                try {
                    if (values.userType === "single") {
                        await printGuestLabel({
                            visitorName: values.guestName,
                            visitorCompany: values.guestCompany,
                            hostName: userDetailsState?.selectedUserName ?? "",
                            checkOutCode: singleCard?.checkOutCode,
                            validTo: formatValidTo(singleCard?.validTo),
                        })
                    }
                    if (values.userType === "group") {
                        await printGroupLabels({
                            groupName: values.guestCompany,
                            groupSize: +values.numberOfGuests,
                            hostName: userDetailsState?.selectedUserName ?? "",
                            guestCards: groupCards,
                        })
                    }
                } catch (error) {
                    // Print failure is surfaced by the print-outcome effect (snackbar).
                    console.error("Printing failed:", error)
                }
            } else {
                // No printing needed, finish immediately
                handleFinish?.()
            }

            helpers.resetForm()
            // Keep the persisted checkbox choices after the reset instead of falling back to defaults.
            const prefs = loadCheckInPrefs()
            helpers.setFieldValue("printGuestCard", prefs.printGuestCard)
            helpers.setFieldValue("sendNotification", prefs.sendNotification)
            setUserDetailsState(defaultUserDetailsState)
        } catch (error) {
            console.error("Check-in failed:", error)
        }
    }

    return (
        <>
            <LabelPrinterExtensionDialog
                open={isInstalledError}
                onClose={clearState}
                variant={LabelPrinterDialogVariant.NOT_INSTALLED}
            />
            <LabelPrinterExtensionDialog
                open={isPrinterNotFound}
                onClose={clearState}
                variant={LabelPrinterDialogVariant.PRINTER_NOT_FOUND}
            />
            <Formik<FormValues>
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ values, setFieldValue, errors, submitCount }) => (
                    <Form>
                        {!alertsApi && (
                            <Box marginBottom="2rem">
                                <CheckInAlerts alerts={alerts} onRemove={removeAlert} />
                            </Box>
                        )}
                        <Stack flexDirection="row" justifyContent="space-between" spacing="2rem" marginBottom="2rem">
                            <Box className={classes.guestDetails}>
                                {variant === "modal" && (
                                    <LocalizedStrict id="service-center-reports-check-in-modal-guest-details">
                                        <Typography variant="subtitle1" gutterBottom>
                                            Guest details
                                        </Typography>
                                    </LocalizedStrict>
                                )}
                                <Stack spacing="1.5rem" marginBottom="3rem">
                                    <Box>
                                        <CompanySelect
                                            userDetails={userDetailsState}
                                            labelTranslationKey="create-order-on-behalf-of-dialog-select-company-host-field"
                                            setUserDetails={(data) => {
                                                const next = typeof data === "function" ? data(userDetailsState) : data
                                                setUserDetailsState(next)
                                                setFieldValue("companyId", next?.companyId ?? "")
                                            }}
                                            scope={scope}
                                        />
                                        {submitCount > 0 && errors.companyId && (
                                            <FormHelperText error>{errors.companyId}</FormHelperText>
                                        )}
                                    </Box>

                                    <Box>
                                        <UserSelector
                                            labelTranslationKey="create-order-on-behalf-of-dialog-select-host-field"
                                            userDetails={userDetailsState}
                                            setUserDetails={(data) => {
                                                const next = typeof data === "function" ? data(userDetailsState) : data
                                                setUserDetailsState(next)
                                                setFieldValue("selectedUserId", next?.selectedUserId)
                                            }}
                                            scope={scope}
                                        />
                                        {submitCount > 0 && errors.selectedUserId && (
                                            <FormHelperText error>{errors.selectedUserId}</FormHelperText>
                                        )}
                                    </Box>
                                </Stack>
                                <UserToggle
                                    value={values.userType}
                                    onChange={(value) => setFieldValue("userType", value)}
                                />
                                {values.userType === "single" ? (
                                    <LocalizedStrict
                                        id="service-center-reports-check-in-modal-guest-name"
                                        attrs={{ label: true }}
                                    >
                                        <FormikTextField<FormValues>
                                            name="guestName"
                                            label="Guest name"
                                            fullWidth
                                            margin="normal"
                                            autoComplete="off"
                                            inputProps={{
                                                "data-1p-ignore": "true",
                                                "data-lpignore": "true",
                                                "data-bwignore": "true",
                                            }}
                                        />
                                    </LocalizedStrict>
                                ) : (
                                    <LocalizedStrict
                                        id="service-center-reports-check-in-modal-number-of-guests"
                                        attrs={{ label: true }}
                                    >
                                        <FormikNumberField<FormValues>
                                            name="numberOfGuests"
                                            min={1}
                                            label="Number of guests"
                                            fullWidth
                                            margin="normal"
                                        />
                                    </LocalizedStrict>
                                )}
                                <LocalizedStrict
                                    id={
                                        values.userType === "single"
                                            ? "service-center-reports-check-in-modal-guest-company"
                                            : "service-center-reports-check-in-modal-guests-company"
                                    }
                                    attrs={{ label: true }}
                                >
                                    <FormikTextField<FormValues>
                                        name="guestCompany"
                                        label="Guest company"
                                        fullWidth
                                        margin="normal"
                                        autoComplete="off"
                                        inputProps={{
                                            "data-1p-ignore": "true",
                                            "data-lpignore": "true",
                                            "data-bwignore": "true",
                                        }}
                                    />
                                </LocalizedStrict>
                            </Box>
                            <Box className={classes.settingsSection}>
                                {variant === "modal" && (
                                    <LocalizedStrict id="service-center-reports-check-in-modal-settings">
                                        <Typography variant="subtitle1" gutterBottom>
                                            Settings
                                        </Typography>
                                    </LocalizedStrict>
                                )}
                                <LocalizedStrict
                                    id="service-center-reports-check-in-modal-print-guest-card"
                                    attrs={{ label: true }}
                                >
                                    <FormControlLabel
                                        className={classes.checkboxLabel}
                                        control={
                                            <Checkbox
                                                className={classes.checkboxAlignTop}
                                                checked={values.printGuestCard}
                                                onChange={(e) => {
                                                    const checked = e.target.checked
                                                    setFieldValue("printGuestCard", checked)
                                                    saveCheckInPrefs({
                                                        printGuestCard: checked,
                                                        sendNotification: values.sendNotification,
                                                    })
                                                }}
                                            />
                                        }
                                        label="Print guest card"
                                    />
                                </LocalizedStrict>
                                <LocalizedStrict
                                    id="service-center-reports-check-in-modal-send-host-notification-tooltip"
                                    attrs={{ title: true }}
                                >
                                    <Tooltip
                                        title="Host will receive notification that the guest has been checked in."
                                        arrow
                                    >
                                        <Box>
                                            <LocalizedStrict
                                                id="service-center-reports-check-in-modal-send-host-notification"
                                                attrs={{ label: true }}
                                            >
                                                <FormControlLabel
                                                    className={classes.checkboxLabel}
                                                    control={
                                                        <Checkbox
                                                            className={classes.checkboxAlignTop}
                                                            checked={values.sendNotification}
                                                            onChange={(e) => {
                                                                const checked = e.target.checked
                                                                setFieldValue("sendNotification", checked)
                                                                saveCheckInPrefs({
                                                                    printGuestCard: values.printGuestCard,
                                                                    sendNotification: checked,
                                                                })
                                                            }}
                                                        />
                                                    }
                                                    label="Notify host"
                                                />
                                            </LocalizedStrict>
                                        </Box>
                                    </Tooltip>
                                </LocalizedStrict>
                                {variant === "modal" && (
                                    <Box display="flex" alignItems="center" marginTop={2}>
                                        <LocalizedStrict
                                            id="service-center-reports-check-in-modal-write-message-to-host"
                                            attrs={{ label: true }}
                                        >
                                            <FormikTextField<FormValues>
                                                name="messageToHost"
                                                label="Write a message to host"
                                                fullWidth
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <FontAwesomeIcon icon={faMessage} />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </LocalizedStrict>
                                    </Box>
                                )}
                            </Box>
                        </Stack>

                        <Stack justifyContent="center" alignItems="center">
                            <LocalizedStrict id="service-center-reports-check-in-modal-check-in-button">
                                <FormikSubmitButton fullWidth={false} className={classes.checkinButton} type="submit">
                                    Check-in
                                </FormikSubmitButton>
                            </LocalizedStrict>
                        </Stack>
                    </Form>
                )}
            </Formik>
        </>
    )
}
